import {
  castArray,
  compact,
  map,
  mapValues,
  partition,
  pick,
  filter,
  fromPairs,
  isEmpty,
  keyBy,
} from "lodash";
import { useMutation } from "react-query";
import { fetchApi } from "../reactQueryApi";

// Narrows form values down to the given file/image fields and normalizes
// each field to a compact array — the shape publishAllVolatile expects.
// Shared by every form that publishes uploads before saving (ItemForm,
// MembershipForm, ContactForm).
export const pickFileValues = (
  values: Record<string, unknown>,
  fields: string[],
): Record<string, unknown[]> =>
  mapValues(pick(values, fields), (files) => compact(castArray(files)));

// Custom Hook that Publishes all files or images with state 'volatile' which exist in values.
// Used before saving items to apps api,before sending message with attachments,
// etc.
//
// @example
//   const publishAllVolatile = usePublishAllVolatile();
//   const result = await publishAllVolatile({
//     values: { profile_image: [{ id: 123, state: "volatile", ... }] },
//     storageDirectory: 'images',
//   });
//
//   // For republishing all files (e.g. used for Duplicating Images/Files by the AppCreator, "Save as New"):
//   const result = await publishAllVolatile({
//     values: { profile_image: [{ id: 123, ... }] },
//     storageDirectory: 'images',
//     republish: true,
//   });
//
//   console.log(result)
//   //=> { profile_image: [{ id: 123, state: "published", token: 456, ... }] }
//
//   // Additional publish params per field, e.g. to have the file_server check an asset
//   // as an image without `as`, which would change its delivery (tixxt/core#694):
//   await publishAllVolatile({
//     values: { emailLogoImage: [{ id: 123, state: "volatile", ... }] },
//     storageDirectory: 'assets',
//     publishParams: { emailLogoImage: { check_image: true } },
//   });
type PublishFilesArgs = {
  body: { [key: string]: any };
  fieldName: string;
  others: File[];
  storageDirectory: string;
};
const usePublishAllVolatile = () => {
  const publishFiles = useMutation<
    { [key: string]: File[] }[],
    unknown,
    PublishFilesArgs
  >(({ body, storageDirectory }: PublishFilesArgs) => {
    return fetchApi(`/api/storage/${storageDirectory}/publish`, {
      method: "POST",
      body,
    });
  });

  const destroyFiles = useMutation(
    ({
      id,
      storageDirectory,
    }: {
      id: string;
      fieldName: string;
      storageDirectory: string;
    }) => {
      return fetchApi(`/api/storage/${storageDirectory}/${id}`, {
        method: "DELETE",
      });
    },
  );

  const publishAllVolatile = async ({
    values,
    storageDirectory,
    republish = false,
    skipDestroy = false,
    publishParams = {},
  }: {
    values: Record<string, any>;
    storageDirectory: string;
    republish?: boolean;
    skipDestroy?: boolean;
    publishParams?: Record<string, Record<string, unknown>>;
  }) => {
    const pairs = await Promise.all(
      map(values, async (files, fieldName) => {
        // When republish is true, treat all files as volatile to regenerate tokens
        const [volatile, nonVolatile] = republish
          ? [files, []]
          : partition(files, { state: "volatile" });
        const [remove, others] = partition(nonVolatile, { state: "removed" });

        if (isEmpty(volatile) && isEmpty(remove)) return [fieldName, files];

        const published = await publishFiles.mutateAsync({
          body: {
            [storageDirectory]: map(volatile, (file) => ({
              ...file,
              ...publishParams[fieldName],
            })),
          },
          storageDirectory,
          fieldName,
          others,
        });

        // When skipDestroy is true (e.g. profile change requests), don't delete
        // files from storage. The old files must remain intact until the change
        // request is approved. Removed files are simply omitted from the result.
        //
        // Only files uploaded in this form session (identified by the clientId
        // assigned on upload) may be destroyed — they are still volatile and
        // exist nowhere else. Files that were already published, e.g. selected
        // from the network's file area via "select existing", must only be
        // dereferenced, never deleted from storage (#184).
        //
        // Invariant this rests on: a clientId means "uploaded in this form
        // session". It is assigned exclusively by the local upload path, never
        // by "select existing", and it does not survive a reload — the server
        // does not serialize it. Every form today navigates away or resets with
        // server data after saving, so a file loaded from the server can never
        // carry one. A form that stays mounted after saving would keep the
        // clientId of its own upload and still destroy that file on removal —
        // correct, since it exists nowhere else, but worth knowing before
        // relying on this filter elsewhere.
        const sessionUploads = filter(remove, "clientId");
        if (!skipDestroy) {
          await Promise.all(
            map(sessionUploads, async (file) => {
              try {
                await destroyFiles.mutateAsync({
                  id: file.id,
                  storageDirectory,
                  fieldName,
                });
              } catch (error) {
                console.warn(`Failed to destroy file ${file.id}:`, error);
              }
            }),
          );
        }

        const publishedById = keyBy(published, "id");
        const result = files
          .filter((file) => !(skipDestroy && file.state === "removed"))
          .map((file) => {
            if (file.state === "removed") return { ...file, _destroy: true };
            return publishedById[file.id] || file;
          });

        return [fieldName, result];
      }),
    );
    return fromPairs(pairs);
  };

  return publishAllVolatile;
};

export default usePublishAllVolatile;
